import { DateTime } from "luxon";

// Utils
import { DEFAULT_LANGUAGE } from "src/config/languages";

// starts with oldest date first
const sortByOldestDate = <T>(array: Array<T>, key: keyof T) =>
  array?.sort(
    (a, b) =>
      DateTime.fromISO(a[key] as string, { zone: "utc" }).toMillis() -
      DateTime.fromISO(b[key] as string, { zone: "utc" }).toMillis()
  );

// starts with newest date first
const sortByDate = <T>(array: Array<T>, key: keyof T) =>
  array?.sort(
    (a, b) =>
      DateTime.fromISO(b[key] as string, { zone: "utc" }).toMillis() -
      DateTime.fromISO(a[key] as string, { zone: "utc" }).toMillis()
  );

// Gets ISO date for only the date portion (no timezone information)
const getISODate = (date: DateTime | null) => {
  if (!date) return "";

  return (
    DateTime.fromISO(date.toFormat("yyyy-MM-dd"), {
      zone: "utc",
    }).toISO() ?? ""
  );
};

// Convert UTC date from backend to YYYY-MM-DD format
const getLocalDate = (utcString: string, timezone: string | null) => {
  let timezoneToUse = timezone;
  if (!timezoneToUse) timezoneToUse = DateTime.local().zoneName;

  const utcDateTime = DateTime.fromISO(utcString, { zone: "utc" });
  const localDateTime = utcDateTime.setZone(timezoneToUse);
  return localDateTime.toFormat("yyyy-MM-dd");
};

// Convert UTC date from backend to YYYY-MM-DD HH:mm:ss ZZZZ format
const getLocalDateTime = (utcString: string, timezone: string | null) => {
  let timezoneToUse = timezone;
  if (!timezoneToUse) timezoneToUse = DateTime.local().zoneName;

  const utcDateTime = DateTime.fromISO(utcString, { zone: "utc" });
  const localDateTime = utcDateTime.setZone(timezoneToUse);
  return localDateTime.toFormat("yyyy-MM-dd HH:mm:ss");
};

// Convert UTC date from backend to calendar date and hours/minutes with timezone abbreviation
const getLocalDateTimeWithZone = (
  utcString: string,
  timezone: string | null
) => {
  let timezoneToUse = timezone;
  if (!timezoneToUse) timezoneToUse = DateTime.local().zoneName;

  const utcDateTime = DateTime.fromISO(utcString, { zone: "utc" });
  const localDateTime = utcDateTime.setZone(timezoneToUse);
  return {
    date: localDateTime.toFormat("yyyy-MM-dd"),
    time: localDateTime.toFormat("HH:mm (ZZZZ)"),
  };
};

// This is to list all timezone abbreviations
const getAllTimeZoneInfo = () => {
  const timeZones = (Intl as any).supportedValuesOf("timeZone");

  return timeZones.map((tz: string) => {
    const offset = new Intl.DateTimeFormat("en", {
      timeZone: tz,
      timeZoneName: "longOffset",
    })
      .formatToParts()
      .find((part) => part.type === "timeZoneName")?.value;
    return { name: `${tz} (${offset})` };
  });
};

// Get start/end times in UTC dates to send to the backend
const getUtcStartEndDate = (days: number, timezone: string | null) => {
  let timezoneToUse = timezone;
  if (!timezoneToUse) timezoneToUse = DateTime.local().zoneName;

  const localTime = DateTime.now().setZone(timezoneToUse);

  // For the start date, get 00:00:00 in local time
  // Subtract the input number of days
  // Then convert to UTC time
  const localMidnight = localTime.startOf("day");
  const startOfUTC = localMidnight.minus({ days }).toUTC().toISO() ?? "";

  // For the end date, get 23:59:59 in local time
  // Then convert to UTC time
  const endOfUTC = localTime.endOf("day").toUTC().toISO() ?? "";

  return { startOfUTC, endOfUTC };
};

const calculateYearDifference = (
  utcString: string | null | undefined,
  timezone?: string | null | undefined
) => {
  if (!utcString) return null;

  const timezoneToUse = timezone ?? "utc";

  // "today" as a calendar date in the given timezone, anchored to midnight UTC
  const todayDateStr = DateTime.now()
    .setZone(timezoneToUse)
    .toFormat("yyyy-MM-dd");
  const todaysDate = DateTime.fromISO(todayDateStr, { zone: "utc" });

  // birthdate as its plain calendar date (the stored value is the intended date), anchored to midnight UTC
  const pastDate = DateTime.fromISO(utcString.slice(0, 10), { zone: "utc" });

  // if the past date is invalid, return null
  if (!pastDate.isValid) return null;

  // get the difference between the past year and current year
  const difference = todaysDate.diff(pastDate, "years");

  // return the value as an integer
  return Math.floor(difference.toObject().years ?? 0);
};

const calculateWeekDifference = (
  utcString: string | null | undefined,
  timezone?: string | null | undefined
) => {
  if (!utcString) return null;

  const timezoneToUse = timezone ?? "utc";

  // "today" as a calendar date in the given timezone, anchored to midnight UTC
  const todayDateStr = DateTime.now()
    .setZone(timezoneToUse)
    .toFormat("yyyy-MM-dd");
  const todaysDate = DateTime.fromISO(todayDateStr, { zone: "utc" });

  // birthdate as its plain calendar date (the stored value is the intended date), anchored to midnight UTC
  const pastDate = DateTime.fromISO(utcString.slice(0, 10), { zone: "utc" });

  // if the past date is invalid, return null
  if (!pastDate.isValid) return null;

  // get the difference between the past year and current year
  const difference = todaysDate.diff(pastDate, "weeks");

  // return the value as an integer
  return Math.floor(difference.toObject().weeks ?? 0);
};

// Checks if a date is in the format YYYY-MM-DD
const isValidDateFormat = (dateString: string) => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  return dateRegex.test(dateString);
};

const calculateAgeFromExamDate = (
  startDate: string | null,
  endDate: string | null
) => {
  if (!startDate || !endDate) return "-";

  const start = DateTime.fromISO(startDate, { zone: "utc" });
  const end = DateTime.fromISO(endDate, { zone: "utc" });

  // if the dates are invalid, return a placeholder string
  if (!end.isValid || !start.isValid) {
    return "-";
  }

  // get the difference between the past year and current year
  const difference = end.diff(start, "years");

  // return the value as an integer
  return Math.floor(difference.toObject().years ?? 0);
};

// Format a plain YYYY-MM-DD date
// ex: "Aug 13, 2026" in English, "13 août 2026" in French.
const getFormattedDisplayDate = (
  dateString: string,
  languageCode: string = DEFAULT_LANGUAGE
) => {
  if (!dateString) return "";

  return DateTime.fromISO(dateString, { zone: "utc" })
    .setLocale(languageCode)
    .toLocaleString(DateTime.DATE_MED);
};

// Format a plain date + time string (no timezone conversion -- just
// localizes the wording/ordering of an already-fixed wall-clock value).
// ex: "January 30, 2023, 15:25" in English, "30 janvier 2023 à 15:25" in French.
const getFormattedDisplayDateTime = (
  dateTimeString: string,
  languageCode: string = DEFAULT_LANGUAGE
) => {
  if (!dateTimeString) return "";
  else if (dateTimeString.includes("T")) {
    return DateTime.fromISO(dateTimeString, { zone: "utc" })
      .setLocale(languageCode)
      .toLocaleString({
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      });
  }

  return DateTime.fromISO(dateTimeString, { zone: "utc" })
    .setLocale(languageCode)
    .toLocaleString({
      month: "long",
      day: "numeric",
      year: "numeric",
    });
};

const getStartOfToday = (timezone: string | null) => {
  let timezoneToUse = timezone;
  if (!timezoneToUse) timezoneToUse = DateTime.local().zoneName;

  return (
    DateTime.now().setZone(timezoneToUse).startOf("day").toUTC().toISO() ?? ""
  );
};

export {
  sortByOldestDate,
  sortByDate,
  getISODate,
  getLocalDate,
  getFormattedDisplayDate,
  getFormattedDisplayDateTime,
  getLocalDateTime,
  getLocalDateTimeWithZone,
  getAllTimeZoneInfo,
  getUtcStartEndDate,
  calculateWeekDifference,
  calculateYearDifference,
  calculateAgeFromExamDate,
  isValidDateFormat,
  getStartOfToday,
};
